import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { Check, X } from "lucide-react";
import {
  COMPETITOR_FEATURES,
  isValueKey,
  valueKey,
  type FeatureValue,
} from "@/data/landing";
import { fadeUp, viewportOnce } from "./motion-presets";

const FeatureCell = ({ value, accent }: { value: FeatureValue; accent?: boolean }) => {
  const { t } = useTranslation("landing");
  if (value === true)
    return <Check className={`h-4 w-4 mx-auto ${accent ? "text-primary" : "text-success"}`} />;
  if (value === false) return <X className="h-4 w-4 text-muted-foreground/40 mx-auto" />;
  const label = isValueKey(value) ? t(`compare.values.${valueKey(value)}`) : value;
  return (
    <span className={`text-xs ${accent ? "text-primary font-semibold" : "text-muted-foreground"}`}>
      {label}
    </span>
  );
};

export const CompareTable = () => {
  const { t } = useTranslation("landing");
  return (
    <section id="compare" className="relative py-24 px-4">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        variants={fadeUp}
        className="container mx-auto max-w-5xl"
      >
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {t("compare.eyebrow")}
          </span>
          <h2 className="mt-3 font-display text-3xl md:text-5xl font-semibold tracking-tight text-foreground">
            {t("compare.title")}
          </h2>
          <p className="mt-4 text-muted-foreground">{t("compare.subtitle")}</p>
        </div>

        <div className="relative rounded-xl border border-border bg-card/30 overflow-hidden">
          {/* Mobile scroll hint — the comparison columns overflow on phones. */}
          <div
            aria-hidden
            className="md:hidden pointer-events-none absolute inset-y-0 right-0 w-10 z-20 bg-gradient-to-l from-background/90 to-transparent"
          />
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="sticky left-0 z-10 bg-background/95 backdrop-blur text-left px-5 py-4 text-xs uppercase tracking-wider text-muted-foreground font-medium">
                    {t("compare.header.feature")}
                  </th>
                  <th className="px-5 py-4 text-center text-xs uppercase tracking-wider text-primary font-semibold">
                    {t("compare.header.workloop")}
                  </th>
                  <th className="px-5 py-4 text-center text-xs uppercase tracking-wider text-muted-foreground font-medium">
                    {t("compare.header.jobber")}
                  </th>
                  <th className="px-5 py-4 text-center text-xs uppercase tracking-wider text-muted-foreground font-medium">
                    {t("compare.header.housecall")}
                  </th>
                  <th className="px-5 py-4 text-center text-xs uppercase tracking-wider text-muted-foreground font-medium">
                    {t("compare.header.servicetitan")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {COMPETITOR_FEATURES.map((row) => (
                  <tr
                    key={row.key}
                    className="border-b border-border last:border-0 transition-colors hover:bg-card/40"
                  >
                    <td className="sticky left-0 z-10 bg-background/95 backdrop-blur px-5 py-4 text-foreground/85">{t(`compare.rows.${row.key}`)}</td>
                    <td className="px-5 py-4 text-center bg-primary/[0.04]">
                      <FeatureCell value={row.workloop} accent />
                    </td>
                    <td className="px-5 py-4 text-center"><FeatureCell value={row.jobber} /></td>
                    <td className="px-5 py-4 text-center"><FeatureCell value={row.housecall} /></td>
                    <td className="px-5 py-4 text-center"><FeatureCell value={row.servicetitan} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </motion.div>
    </section>
  );
};
